import { useState } from 'react';
import { Banner, Button, Dialog, Skeleton, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { lockPortal, unlockPortal, useInvalidatePortal, type StaffPortalCard } from '../portal-api';
import { PortalInviteAction } from './PortalInviteAction';
import { PortalPreviewDialog } from './PortalPreviewDialog';
import record from './record.module.css';
import styles from './portal.module.css';

export interface PortalCardProps {
  contactId: string;
  contactName: string;
  /** The contact's e-mail of the record head, named in the invitation's confirmation. */
  email: string | null;
  card: StaffPortalCard | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}

const STATE_CLASS: Record<StaffPortalCard['status']['state'], string | undefined> = {
  active: styles.stateActive,
  invited: styles.stateInvited,
  created: styles.stateInvited,
  locked: styles.stateLocked,
  none: styles.stateNone,
};

type Busy = 'lock' | 'unlock' | null;

/**
 * Mandantenportal 27 „Portalstatus" (right column of the record): access state, last login, open portal tasks;
 * „Portalansicht öffnen" (read-only preview), „Portal einladen" / „Einladung erneut senden" (confirmation, D-334),
 * „Zugang sperren" (confirmation) and „Zugang entsperren". Every button follows the server's `actions`; a refusal
 * shows its reason.
 */
export function PortalCard({
  contactId,
  contactName,
  email,
  card,
  loading,
  error,
  onRetry,
}: PortalCardProps) {
  const { toast } = useToast();
  const invalidate = useInvalidatePortal();
  const [busy, setBusy] = useState<Busy>(null);
  const [confirmLock, setConfirmLock] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  async function run(kind: Exclude<Busy, null>) {
    setBusy(kind);
    try {
      if (kind === 'lock') {
        await lockPortal(contactId);
        setConfirmLock(false);
        toast({ message: 'Zugang gesperrt.' });
      } else {
        await unlockPortal(contactId);
        toast({ message: 'Zugang entsperrt.' });
      }
      await invalidate(contactId);
    } catch (cause) {
      if (kind === 'lock') setLockError(errorMessage(cause));
      else toast({ message: errorMessage(cause) });
    } finally {
      setBusy(null);
    }
  }

  const state = card?.status.state ?? 'none';
  const actions = card?.actions;
  const showInvite = state === 'none' || state === 'created' || state === 'invited';
  const refusal =
    actions === undefined
      ? null
      : showInvite && !actions.invite.allowed
        ? actions.invite.reason
        : state === 'locked' && !actions.unlock.allowed
          ? actions.unlock.reason
          : null;

  return (
    <section className={record.card} aria-label="Portalstatus">
      <div className={styles.eyebrow}>Portalstatus</div>
      {loading ? (
        <div className={styles.skeletonRows} aria-busy="true">
          <Skeleton width="80%" />
          <Skeleton width="70%" />
          <Skeleton width="75%" />
        </div>
      ) : null}
      {error ? (
        <div className={styles.skeletonRows}>
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={onRetry}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(error)}
          </Banner>
        </div>
      ) : null}
      {card ? (
        <>
          <dl className={styles.rows}>
            <div className={styles.kv}>
              <dt>Zugang</dt>
              <dd className={STATE_CLASS[state]}>{card.status.label}</dd>
            </div>
            <div className={styles.kv}>
              <dt>Letzter Login</dt>
              <dd>{card.status.lastLogin ?? '—'}</dd>
            </div>
            <div className={styles.kv}>
              <dt>Offene Aufgaben im Portal</dt>
              <dd>{card.status.openTasks}</dd>
            </div>
          </dl>
          <div className={styles.actions}>
            {card.actions.preview.allowed ? (
              <Button variant="secondary" size="small" onClick={() => setPreview(true)}>
                Portalansicht öffnen
              </Button>
            ) : null}
            {showInvite ? (
              <PortalInviteAction contactId={contactId} contactName={contactName} email={email} card={card} />
            ) : null}
            {card.actions.lock.allowed ? (
              <Button
                variant="ghost"
                size="small"
                onClick={() => {
                  setLockError(null);
                  setConfirmLock(true);
                }}
              >
                Zugang sperren
              </Button>
            ) : null}
            {state === 'locked' ? (
              <Button
                variant="secondary"
                size="small"
                disabled={!card.actions.unlock.allowed}
                loading={busy === 'unlock'}
                onClick={() => void run('unlock')}
              >
                Zugang entsperren
              </Button>
            ) : null}
          </div>
          {refusal ? <p className={styles.reason}>{refusal}</p> : null}
        </>
      ) : null}

      <Dialog
        open={confirmLock}
        onClose={() => setConfirmLock(false)}
        width={560}
        tone="danger"
        title="Zugang sperren"
        subtitle={contactName}
        initialFocus="first"
        footer={
          <>
            <Button variant="secondary" disabled={busy === 'lock'} onClick={() => setConfirmLock(false)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={busy === 'lock'} onClick={() => void run('lock')}>
              Zugang sperren
            </Button>
          </>
        }
      >
        {lockError ? <Banner tone="danger">{lockError}</Banner> : null}
        <p className={styles.confirmText}>
          Der Kunde wird sofort abgemeldet und kann sich nicht mehr anmelden.
        </p>
      </Dialog>
      <PortalPreviewDialog
        open={preview}
        contactId={contactId}
        contactName={contactName}
        onClose={() => setPreview(false)}
      />
    </section>
  );
}
